import { createApi } from "@reduxjs/toolkit/query/react";
import { AgentStateDto } from "../../models/agent";
import { apiBaseQuery, post, put } from "../queryHelpers";

export interface BusinessUnit {
  id: string;
  enabled: boolean;
  name?: string;
}

export interface CompleteWorkItemParams {
  workItemId: string;
  closureCode: string | null;
  notes: string | null;
}

const blenderApi = createApi({
  reducerPath: "blenderApi",
  tagTypes: ["AgentState", "BusinessUnits"],
  baseQuery: apiBaseQuery("/blender/resource"),
  endpoints: builder => ({
    getAgentState: builder.query<AgentStateDto, void>({
      query: () => "/getstate",
      providesTags: ["AgentState"],
    }),
    login: builder.mutation<void, void>({
      query: () => post("/login?mobileSession=true", {}),
      invalidatesTags: ["AgentState"],
    }),
    logout: builder.mutation<void, void>({
      query: () => post("/logout", {}),
      invalidatesTags: ["AgentState"],
    }),
    goToAvailable: builder.mutation<void, void>({
      query: () => post("/activate", {}),
      invalidatesTags: ["AgentState"],
    }),
    getBreakNames: builder.query<string[], void>({
      query: () => "/breakNames",
    }),
    goToBreak: builder.mutation<void, string | null>({
      query: breakName => post(`/moveToBreak?breakName=${breakName ?? ""}`, {}),
      invalidatesTags: ["AgentState"],
    }),
    getBusinessUnits: builder.query<BusinessUnit[], void>({
      query: () => "/businessUnits",
      providesTags: ["BusinessUnits"],
    }),
    updateBusinessUnits: builder.mutation<void, BusinessUnit>({
      query: businessUnits => put("/businessUnits", [businessUnits]), // manually map to array because the API could actually handle more than one BU change at a time
      invalidatesTags: ["BusinessUnits"],
    }),
    reserveForCallLookup: builder.mutation<string | null, { agentWorkspaceId: string | null }>({
      query: ({ agentWorkspaceId }) => post(`/reserveForCallLookup?agentWorkspaceId=${agentWorkspaceId ?? ""}`, {}),
      transformResponse: (response: { workItemId: string }) => response.workItemId,
    }),
    completeWorkItem: builder.mutation<void, CompleteWorkItemParams>({
      query: params => post("/completeWorkItem", params),
    }),
  }),
});

export const {
  useGetAgentStateQuery,
  useLazyGetAgentStateQuery,
  useLoginMutation,
  useLogoutMutation,
  useGoToAvailableMutation,
  useGetBreakNamesQuery,
  useLazyGetBreakNamesQuery,
  useGoToBreakMutation,
  useGetBusinessUnitsQuery,
  useUpdateBusinessUnitsMutation,
  useReserveForCallLookupMutation,
  useCompleteWorkItemMutation,
} = blenderApi;

export default blenderApi;
